import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Input from "../../../components/ui/Input";
import API_BASE_URL from "../../../services/api";

/* =========================
   DOB / AGE HELPERS
========================= */
const pad2 = (n) => String(n).padStart(2, "0");

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

const daysInMonth = (m, y) => {
  try {
    const mm = Number(m);
    const yy = Number(y);
    if ([1, 3, 5, 7, 8, 10, 12].includes(mm)) return 31;
    if ([4, 6, 9, 11].includes(mm)) return 30;
    if (mm === 2) return isLeap(yy) ? 29 : 28;
    return 0;
  } catch (e) {
    return 0;
  }
};

const parseDDMMYYYY = (s) => {
  try {
    if (!s) return null;
    const str = String(s).trim();
    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;

    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);

    if (!yyyy || yyyy < 1900 || yyyy > 2100) return null;
    if (!mm || mm < 1 || mm > 12) return null;

    const dim = daysInMonth(mm, yyyy);
    if (!dd || dd < 1 || dd > dim) return null;

    // UTC date to avoid timezone shifts
    return new Date(Date.UTC(yyyy, mm - 1, dd));
  } catch (e) {
    return null;
  }
};

const toISODateString = (dateObj) => {
  try {
    if (!(dateObj instanceof Date)) return null;
    const y = dateObj.getUTCFullYear();
    const m = pad2(dateObj.getUTCMonth() + 1);
    const d = pad2(dateObj.getUTCDate());
    return `${y}-${m}-${d}`; // YYYY-MM-DD
  } catch (e) {
    return null;
  }
};

const isoToDDMMYYYY = (iso) => {
  try {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    return `${m[3]}/${m[2]}/${m[1]}`;
  } catch (e) {
    return "";
  }
};

// ✅ years+months+days (supports babies)
const calculateAgeBreakdownFromDOB = (dobUTCDate) => {
  try {
    if (!(dobUTCDate instanceof Date)) return { years: 0, months: 0, days: 0 };

    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const dobLocal = new Date(
      dobUTCDate.getUTCFullYear(),
      dobUTCDate.getUTCMonth(),
      dobUTCDate.getUTCDate()
    );

    if (dobLocal > todayLocal) return { years: 0, months: 0, days: 0 };

    let years = todayLocal.getFullYear() - dobLocal.getFullYear();
    let months = todayLocal.getMonth() - dobLocal.getMonth();
    let days = todayLocal.getDate() - dobLocal.getDate();

    if (days < 0) {
      // borrow from previous month
      const lastDayPrevMonth = new Date(
        todayLocal.getFullYear(),
        todayLocal.getMonth(),
        0
      ).getDate();
      days += lastDayPrevMonth;
      months -= 1;
    }

    if (months < 0) {
      months += 12;
      years -= 1;
    }

    if (years < 0) return { years: 0, months: 0, days: 0 };

    return { years, months, days };
  } catch (e) {
    console.error(e);
    return { years: 0, months: 0, days: 0 };
  }
};

// formats as DD/MM/YYYY while typing (digits only)
const formatDOBInput = (raw) => {
  try {
    const digits = String(raw || "")
      .replace(/\D/g, "")
      .slice(0, 8); // ddmmyyyy
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);

    let out = dd;
    if (digits.length > 2) out += `/${mm}`;
    if (digits.length > 4) out += `/${yyyy}`;
    return out;
  } catch (e) {
    return raw || "";
  }
};

const NewPatientPage = () => {
  const navigate = useNavigate();

  // "age" OR "dob"
  const [ageOrDobMode, setAgeOrDobMode] = useState("age");

  const [formData, setFormData] = useState({
    mobile: "",
    initial: "Mr.",
    name: "",
    gender: "",
    age: "",
    email: "",
    dob: "", // UI stores DD/MM/YYYY
    placeOfBirth: "", // optional
    aadharNo: "",
    passportNo: "",
    address: "",
  });

  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [patientChecked, setPatientChecked] = useState(false);
  const [existingPatient, setExistingPatient] = useState(null);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field, value) => {
    try {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (validationErrors[field]) {
        setValidationErrors((prev) => ({ ...prev, [field]: "" }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleModeChange = (mode) => {
    try {
      setAgeOrDobMode(mode);
      setValidationErrors((prev) => ({ ...prev, age: "", dob: "" }));
    } catch (e) {
      console.error(e);
    }
  };

  // ✅ derive years+months+days from DOB
  const dobDerived = useMemo(() => {
    try {
      const dt = parseDDMMYYYY(formData.dob);
      if (!dt) return { years: 0, months: 0, days: 0 };
      return calculateAgeBreakdownFromDOB(dt);
    } catch (e) {
      return { years: 0, months: 0, days: 0 };
    }
  }, [formData.dob]);

  // ✅ Validation: require Name + Gender + (Age OR DOB)
  const validateRequiredFields = () => {
    const errors = {};

    try {
      if (!formData.name.trim()) errors.name = "Name is required";
      if (!formData.gender) errors.gender = "Gender is required";

      if (ageOrDobMode === "age") {
        const ageNum = Number(formData.age);
        // ✅ allow 0 for babies (newborn)
        if (!Number.isFinite(ageNum) || ageNum < 0)
          errors.age = "Valid age is required (0-120)";
        else if (ageNum > 120) errors.age = "Age must be 120 or less";
      } else {
        const dt = parseDDMMYYYY(formData.dob);
        if (!dt) {
          errors.dob = "Please enter DOB in DD/MM/YYYY";
        } else {
          const today = new Date();
          const dobLocal = new Date(
            dt.getUTCFullYear(),
            dt.getUTCMonth(),
            dt.getUTCDate()
          );
          const todayLocal = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );

          if (dobLocal > todayLocal) {
            errors.dob = "DOB cannot be a future date";
          } else {
            const derived = calculateAgeBreakdownFromDOB(dt);
            // ✅ newborn is valid (years=0 ok)
            if (derived.years > 120) errors.dob = "Age from DOB must be 120 or less";
          }
        }
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    } catch (e) {
      console.error(e);
      setValidationErrors({ general: "Validation failed. Please try again." });
      return false;
    }
  };

  // Check if patient exists by mobile
  const checkPatient = async () => {
    if (formData.mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/api/patients/by-mobile?mobile=${formData.mobile}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await res.json();

      if (data.exists && data.patient) {
        setExistingPatient(data.patient);
        setPatientChecked(true);
        setShowPatientForm(true);

        const uiDob = data.patient.dob ? isoToDDMMYYYY(data.patient.dob) : "";

        setFormData((prev) => ({
          ...prev,
          name: data.patient.fullName || "",
          gender: data.patient.gender || "",
          age: data.patient.age ?? "",
          email: data.patient.email || "",
          dob: uiDob,
          initial: data.patient.initial || "Mr.",
          address: data.patient.address || "",
          placeOfBirth: data.patient.placeOfBirth || "",
          aadharNo: data.patient.aadharNo || "",
          passportNo: data.patient.passportNo || "",
        }));

        if (uiDob) setAgeOrDobMode("dob");
        else setAgeOrDobMode("age");
      } else {
        setExistingPatient(null);
        setPatientChecked(true);
        setShowPatientForm(true);
        setAgeOrDobMode("age");
      }
    } catch (error) {
      console.error("Patient fetch error:", error);
      alert("Error checking patient. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Create or update patient
  const handleSavePatient = async () => {
    try {
      if (!formData.mobile || formData.mobile.length !== 10) {
        alert("Please enter a valid 10-digit mobile number.");
        return;
      }

      const ok = validateRequiredFields();
      if (!ok) {
        try {
          const firstErrorField = Object.keys(validationErrors)[0];
          const errorElement = document.querySelector(
            `[data-field="${firstErrorField}"]`
          );
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch (e) {}
        return;
      }

      setLoading(true);

      let dobISO = null;
      let ageToSend = null;

      if (ageOrDobMode === "dob") {
        const dt = parseDDMMYYYY(formData.dob);
        dobISO = dt ? toISODateString(dt) : null;

        // ✅ backend "age" stays in years (0 is valid for babies)
        ageToSend = Number.isFinite(dobDerived.years) ? dobDerived.years : null;
      }  else {
  ageToSend = Number(formData.age);
  if (!Number.isFinite(ageToSend)) ageToSend = null;

  // Derive DOB as Jan 1st of the birth year based on entered age
  if (ageToSend !== null) {
    const birthYear = new Date().getFullYear() - ageToSend;
    dobISO = `${birthYear}-01-01`;
  } else {
    dobISO = null;
  }
}
      let patientId;

      if (existingPatient) {
        const payload = {
          fullName: formData.name,
          initial: formData.initial,
          gender: formData.gender,
          age: ageToSend,
          email: formData.email || null,
          dob: dobISO,
          placeOfBirth: formData.placeOfBirth || null,
          aadharNo: formData.aadharNo || null,
          passportNo: formData.passportNo || null,
          address: formData.address || null,
        };

        const res = await fetch(
          `${API_BASE_URL}/api/patients/${existingPatient.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await res.json();

        if (data) {
          patientId = existingPatient.id;
        } else {
          alert("Failed to update patient: " + (data.message || "Unknown error"));
          return;
        }
      } else {
        const payload = {
          contactNo: formData.mobile,
          fullName: formData.name,
          initial: formData.initial,
          gender: formData.gender,
          age: ageToSend,
          email: formData.email || null,
          dob: dobISO,
          placeOfBirth: formData.placeOfBirth || null,
          aadharNo: formData.aadharNo || null,
          passportNo: formData.passportNo || null,
          address: formData.address || null,
          city: "Mysore",
          state: "Karnataka",
          pincode: "560061",
        };

        const res = await fetch(`${API_BASE_URL}/api/patients`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.success && data.patient) {
          patientId = data.patient.id;
        } else {
          alert("Failed to create patient: " + (data.message || "Unknown error"));
          return;
        }
      }

      navigate(`/orders/new?patientId=${patientId}`);
    } catch (error) {
      console.error("Error saving patient:", error);
      alert("Network error while saving patient.");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuToggle = () => {
    try {
      setSidebarOpen(!sidebarOpen);
    } catch (e) {}
  };

  const handleSidebarClose = () => {
    try {
      setSidebarOpen(false);
    } catch (e) {}
  };

  const handleMobileChange = (value) => {
    try {
      const v = String(value || "").replace(/\D/g, "").slice(0, 10);
      handleChange("mobile", v);

      if (v.length !== 10) {
        setPatientChecked(false);
        setExistingPatient(null);
        setShowPatientForm(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCheckAnother = () => {
    try {
      setFormData({
        mobile: "",
        initial: "Mr.",
        name: "",
        gender: "",
        age: "",
        email: "",
        dob: "",
        placeOfBirth: "",
        aadharNo: "",
        passportNo: "",
        address: "",
      });
      setAgeOrDobMode("age");
      setPatientChecked(false);
      setExistingPatient(null);
      setShowPatientForm(false);
      setShowMoreDetails(false);
      setValidationErrors({});
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          <div className="min-h-screen">
            {/* Page Header */}
            <div className="bg-white shadow border-b mb-4">
              <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="w-7 h-7 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:bg-blue-700 transition-colors"
                  >
                    ←
                  </button>
                  <h1 className="text-base font-semibold text-gray-800">
                    NEW PATIENT
                  </h1>
                </div>
                {existingPatient && (
                  <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    Existing Patient
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {/* Mobile Check Section */}
              <div className="bg-white rounded border shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h2 className="text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Check Patient
                  </h2>
                </div>
                <div className="p-4">
                  <div className="max-w-lg">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Mobile Number *
                    </label>
                    <div className="flex gap-2">
                      <div className="flex flex-1">
                        <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 rounded-l text-xs font-medium text-gray-600">
                          +91
                        </span>
                        <Input
                          type="tel"
                          value={formData.mobile}
                          onChange={(e) => handleMobileChange(e.target.value)}
                          placeholder="Enter 10-digit mobile number"
                          className="rounded-l-none flex-1"
                          maxLength="10"
                          required
                          compact
                          disabled={showPatientForm}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={checkPatient}
                        disabled={
                          formData.mobile.length !== 10 || loading || showPatientForm
                        }
                        className="px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {loading ? "Checking..." : "Check Patient"}
                      </button>
                    </div>

                    {showPatientForm && (
                      <div className="mt-3 flex items-center gap-2">
                        <div
                          className={`text-xs px-2 py-1 rounded ${
                            existingPatient
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {existingPatient ? "✓ Patient exists" : "✗ No existing patient found"}
                        </div>
                        <button
                          type="button"
                          onClick={handleCheckAnother}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Check another number
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Patient Form Section */}
              {showPatientForm && (
                <div className="bg-white rounded border shadow-sm animate-fadeIn">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h2 className="text-xs font-medium text-gray-700 uppercase tracking-wider">
                      {existingPatient ? "Update Patient Details" : "Create New Patient"}
                    </h2>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* ✅ ALIGNED GRID ROW */}
                    <div className="grid grid-cols-12 gap-3 items-start">
                      {/* Title */}
                      <div className="col-span-4 sm:col-span-3 lg:col-span-1 flex flex-col">
                        <Input
                          label="Title"
                          type="select"
                          value={formData.initial}
                          onChange={(e) => handleChange("initial", e.target.value)}
                          compact
                        >
                          <option>Mr.</option>
                          <option>Ms.</option>
                          <option>Mrs.</option>
                          <option>Dr.</option>
                          <option>Baby</option>
                        </Input>
                      </div>

                      {/* Name */}
                      <div
                        className="col-span-8 sm:col-span-9 lg:col-span-4 flex flex-col"
                        data-field="name"
                      >
                        <Input
                          label="Name *"
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          placeholder="Full name"
                          required
                          compact
                          error={validationErrors.name}
                        />
                      </div>

                      {/* Provide (Age or DOB) */}
                      <div className="col-span-12 sm:col-span-6 lg:col-span-2 flex flex-col">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Provide (Age or DOB) *
                        </label>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleModeChange("age")}
                            className={`px-3 py-2 text-xs rounded border transition-colors ${
                              ageOrDobMode === "age"
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            Age
                          </button>

                          <button
                            type="button"
                            onClick={() => handleModeChange("dob")}
                            className={`px-3 py-2 text-xs rounded border transition-colors ${
                              ageOrDobMode === "dob"
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            DOB (DD/MM/YYYY)
                          </button>
                        </div>

                        {/* reserve height so other columns align */}
                        <div className="h-[16px] mt-1" />
                      </div>

                      {/* Age OR DOB */}
                      {ageOrDobMode === "age" ? (
                        <div
                          className="col-span-6 sm:col-span-3 lg:col-span-2 flex flex-col"
                          data-field="age"
                        >
                          <Input
                            label="Age *"
                            type="number"
                            value={formData.age}
                            onChange={(e) => {
                              try {
                                const v = e.target.value;
                                if (v === "") return handleChange("age", "");
                                const n = Number(v);
                                // ✅ allow 0-120
                                if (!Number.isNaN(n) && n >= 0 && n <= 120)
                                  handleChange("age", v);
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            placeholder="0"
                            min="0"
                            max="120"
                            compact
                            required
                            error={validationErrors.age}
                          />
                          <div className="h-[16px] mt-1" />
                        </div>
                      ) : (
                        <div
                          className="col-span-6 sm:col-span-3 lg:col-span-2 flex flex-col"
                          data-field="dob"
                        >
                          <Input
                            label="Date of Birth *"
                            type="text"
                            placeholder="DD/MM/YYYY"
                            value={formData.dob}
                            onChange={(e) => {
                              try {
                                const formatted = formatDOBInput(e.target.value);
                                handleChange("dob", formatted);
                              } catch (err) {
                                console.error(err);
                                handleChange("dob", e.target.value);
                              }
                            }}
                            inputMode="numeric"
                            compact
                            required
                            error={validationErrors.dob}
                          />

                          <div className="h-[16px] mt-1 text-[11px] text-gray-500">
                            {formData.dob && parseDDMMYYYY(formData.dob) ? (
                              <>
                                Derived Age:{" "}
                                <span className="font-medium text-gray-700">
                                  {dobDerived.years > 0
                                    ? `${dobDerived.years} year${
                                        dobDerived.years > 1 ? "s" : ""
                                      }`
                                    : `${dobDerived.months} month${
                                        dobDerived.months !== 1 ? "s" : ""
                                      }${
                                        dobDerived.days
                                          ? `, ${dobDerived.days} day${
                                              dobDerived.days !== 1 ? "s" : ""
                                            }`
                                          : ""
                                      }`}
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {/* Gender */}
                      <div
                        className="col-span-6 sm:col-span-6 lg:col-span-3 flex flex-col"
                        data-field="gender"
                      >
                        <Input
                          label="Gender *"
                          type="select"
                          value={formData.gender}
                          onChange={(e) => handleChange("gender", e.target.value)}
                          compact
                          required
                          error={validationErrors.gender}
                        >
                          <option value="">Select Gender</option>
                          <option>Male</option>
                          <option>Female</option>
                          <option>Other</option>
                        </Input>
                        <div className="h-[16px] mt-1" />
                      </div>

                      {/* Email */}
                      <div className="col-span-12 lg:col-span-4 flex flex-col">
                        <Input
                          label="Email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="patient@example.com"
                          compact
                        />
                      </div>

                      {/* Address */}
                      <div className="col-span-12 lg:col-span-8 flex flex-col">
                        <Input
                          label="Address"
                          value={formData.address}
                          onChange={(e) => handleChange("address", e.target.value)}
                          placeholder="Full address"
                          rows={2}
                          compact
                        />
                      </div>
                    </div>

                    {/* Expandable Section */}
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowMoreDetails(!showMoreDetails)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 transition-colors"
                      >
                        <span>{showMoreDetails ? "Hide" : "+ Add More Details"}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${
                            showMoreDetails ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {showMoreDetails && (
                        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                          {/* Place of Birth (Optional) */}
                          <div>
                            <Input
                              label="Place of Birth (Optional)"
                              type="text"
                              value={formData.placeOfBirth}
                              onChange={(e) => handleChange("placeOfBirth", e.target.value)}
                              placeholder="City / Town"
                              compact
                            />
                          </div>

                          <div>
                            <Input
                              label="Aadhar Number"
                              type="text"
                              value={formData.aadharNo}
                              onChange={(e) => handleChange("aadharNo", e.target.value)}
                              placeholder="12-digit Aadhar"
                              compact
                            />
                          </div>

                          <div>
                            <Input
                              label="Passport Number"
                              type="text"
                              value={formData.passportNo}
                              onChange={(e) => handleChange("passportNo", e.target.value)}
                              placeholder="Passport number"
                              compact
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="px-4 py-3 border-t bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-end gap-3">
                      <button
                        type="button"
                        className="px-6 py-2 border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSavePatient}
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                      >
                        {loading
                          ? "Saving..."
                          : existingPatient
                          ? "Update & Proceed to Order"
                          : "Create Patient & Proceed to Order"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!showPatientForm && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                    <svg
                      className="w-8 h-8 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Check Patient First
                  </h3>
                  <p className="text-xs text-gray-500 max-w-md mx-auto">
                    Enter the patient's mobile number and click "Check Patient" to verify if
                    they exist in the system or create a new patient record.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NewPatientPage;